// =============================================================================
// src/engine/gripSearch.ts
// Generates and ranks all valid grips (string combinations) across a fret
// range and set of active-control combinations.
//
// Scoring (lower = more playable):
//   +1  per skipped string within the grip span
//   +2  per fret of span across the grip
//   +1  per active control engaged (complexity)
//   +0.5 bonus penalty per non-adjacent string pair
// =============================================================================

import type { Copedant, Grip, GripString, GripSearchOptions } from "./types";
import { applyAtFret } from "./applyChanges";
import { chordFromStrings } from "./chordDetect";

// ---------------------------------------------------------------------------
// Combination helper (no external deps)
// ---------------------------------------------------------------------------

/** Yield all k-combinations of `arr`. */
function* combinations<T>(arr: T[], k: number): Generator<T[]> {
  if (k === 0) { yield []; return; }
  if (k > arr.length) { return; }
  const [first, ...rest] = arr;
  for (const combo of combinations(rest, k - 1)) yield [first, ...combo];
  yield* combinations(rest, k);
}

/** Power set of `arr` filtered to sizes [minK, maxK]. */
function* subsetsInRange<T>(arr: T[], minK: number, maxK: number): Generator<T[]> {
  for (let k = minK; k <= Math.min(maxK, arr.length); k++) {
    yield* combinations(arr, k);
  }
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/**
 * Score a grip — lower is more playable.
 * Returns { score, breakdown } for transparency.
 */
export function rankGrip(grip: Grip): { score: number; breakdown: string } {
  const indices = grip.strings.map(s => s.stringIndex).sort((a, b) => a - b);
  const frets = grip.strings.map(s => s.fret);
  const minFret = Math.min(...frets);
  const maxFret = Math.max(...frets);
  const fretSpan = maxFret - minFret;

  // Count skipped strings within span
  let skippedStrings = 0;
  for (let i = 1; i < indices.length; i++) {
    skippedStrings += indices[i] - indices[i - 1] - 1;
  }

  // Non-adjacent pairs penalty
  let nonAdjacentPairs = 0;
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] - indices[i - 1] > 1) nonAdjacentPairs++;
  }

  const controlPenalty = grip.activeControls.length;
  const score =
    skippedStrings * 1 +
    fretSpan * 2 +
    controlPenalty * 1 +
    nonAdjacentPairs * 0.5;

  const breakdown = [
    `skipped×${skippedStrings}`,
    `span×${fretSpan}`,
    `controls×${controlPenalty}`,
    `nonAdj×${nonAdjacentPairs}`,
  ].join(", ");

  return { score, breakdown };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate all playable grips for a copedant across the requested fret range
 * and control combinations.
 *
 * WARNING: the search space can be large.  Sensible defaults keep it fast.
 *
 * @param copedant   The instrument definition.
 * @param activeControlSets  Which control combinations to evaluate.
 *                   Pass [[]] for open/no pedals only, or enumerate desired
 *                   combos, e.g. [[], ["A"], ["B"], ["A","B"]].
 * @param options    Search constraints (fret range, grip size, etc.).
 */
export function generateGrips(
  copedant: Copedant,
  activeControlSets: string[][],
  options: GripSearchOptions = {}
): Grip[] {
  const {
    fretRange = [0, 12],
    gripSizeRange = [3, 4],
    requireQualities,
    maxFretSpan = 4,
  } = options;

  const [minFret, maxFret] = fretRange;
  const [minSize, maxSize] = gripSizeRange;
  const stringCount = copedant.openTuning.length;
  const stringIndices = Array.from({ length: stringCount }, (_, i) => i);

  const results: Grip[] = [];

  for (const activeControls of activeControlSets) {
    for (let fret = minFret; fret <= maxFret; fret++) {
      const applied = applyAtFret(copedant, fret, activeControls);

      // Try every string-index combination of the requested sizes
      for (const combo of subsetsInRange(stringIndices, minSize, maxSize)) {
        // Fret span check (all strings are at the same fret on pedal steel,
        // but we keep the check for future multi-fret grip support)
        const comboFrets = combo.map(() => fret);
        const fretSpan = Math.max(...comboFrets) - Math.min(...comboFrets);
        if (fretSpan > maxFretSpan) continue;

        // Build grip strings
        const gripStrings: GripString[] = combo.map(idx => ({
          stringIndex: idx,
          fret,
          midi: applied.strings[idx].effectiveMidi,
          noteName: applied.strings[idx].noteName,
        }));

        // Detect chord
        const chord = chordFromStrings(applied, combo);

        // Quality filter
        if (requireQualities && chord) {
          if (!requireQualities.includes(chord.quality)) continue;
        }
        if (requireQualities && !chord) continue;

        // Build the grip object (score filled in below)
        const grip: Grip = {
          fret,
          activeControls,
          strings: gripStrings,
          chord,
          score: 0,
          scoreBreakdown: "",
        };

        const { score, breakdown } = rankGrip(grip);
        grip.score = score;
        grip.scoreBreakdown = breakdown;

        results.push(grip);
      }
    }
  }

  // Sort: best (lowest score) first; break ties by fret (lower first)
  results.sort((a, b) => a.score - b.score || a.fret - b.fret);
  return results;
}
